import dayjs from 'dayjs';
import type { Activity, WorkbenchAction, WorkbenchState } from './types';

const newActivity = (
  state: WorkbenchState,
  title: string,
  description: string,
  entityId?: string,
): Activity => {
  const entity = state.sourceEntities.find((item) => item.id === entityId);
  return {
    id: `activity-user-${Date.now()}-${state.activities.length}`,
    title,
    description,
    sourceApp: entity?.sourceApp ?? 'general',
    entityId,
    objectName: entity?.name ?? '投资工作台',
    occurredAt: new Date().toISOString(),
    category: 'internal',
    read: false,
    tags: ['操作记录'],
  };
};

const prependActivity = (state: WorkbenchState, activity: Activity) =>
  [activity, ...state.activities].slice(0, 120);

export const workbenchReducer = (
  state: WorkbenchState,
  action: WorkbenchAction,
): WorkbenchState => {
  switch (action.type) {
    case 'workItem/complete': {
      const task = state.workItems.find(
        (item) => item.id === action.payload.id,
      );
      if (!task || task.status === 'completed') return state;
      return {
        ...state,
        workItems: state.workItems.map((item) =>
          item.id === action.payload.id
            ? { ...item, status: 'completed' }
            : item,
        ),
        activities: prependActivity(
          state,
          newActivity(
            state,
            `完成待办：${task.title}`,
            '任务状态已同步到投资工作台。',
            task.entityId,
          ),
        ),
      };
    }
    case 'workItem/postpone': {
      const task = state.workItems.find(
        (item) => item.id === action.payload.id,
      );
      if (!task) return state;
      const dueAt =
        action.payload.dueAt ??
        (task.dueAt ? dayjs(task.dueAt) : dayjs()).add(2, 'day').toISOString();
      return {
        ...state,
        workItems: state.workItems.map((item) =>
          item.id === task.id ? { ...item, dueAt } : item,
        ),
        activities: prependActivity(
          state,
          newActivity(
            state,
            `延期待办：${task.title}`,
            `截止时间调整为 ${dayjs(dueAt).format('YYYY-MM-DD HH:mm')}。`,
            task.entityId,
          ),
        ),
      };
    }
    case 'workItem/reassign':
      return {
        ...state,
        workItems: state.workItems.map((item) =>
          action.payload.ids.includes(item.id)
            ? { ...item, owner: action.payload.owner }
            : item,
        ),
      };
    case 'workItem/create':
      return {
        ...state,
        workItems: [action.payload, ...state.workItems],
        activities: prependActivity(
          state,
          newActivity(
            state,
            `新建待办：${action.payload.title}`,
            '已加入统一待办队列。',
            action.payload.entityId,
          ),
        ),
      };
    case 'schedule/create':
      return {
        ...state,
        schedules: [action.payload, ...state.schedules],
        activities: prependActivity(
          state,
          newActivity(
            state,
            `新建日程：${action.payload.title}`,
            `${action.payload.date} ${action.payload.startTime}`,
            action.payload.entityId,
          ),
        ),
      };
    case 'schedule/update':
      return {
        ...state,
        schedules: state.schedules.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        ),
      };
    case 'schedule/complete':
      return {
        ...state,
        schedules: state.schedules.map((item) =>
          item.id === action.payload.id ? { ...item, completed: true } : item,
        ),
      };
    case 'focus/toggle': {
      const entity = state.sourceEntities.find(
        (item) => item.id === action.payload.entityId,
      );
      if (!entity) return state;
      const existing = state.focusObjects.some(
        (item) => item.entityId === entity.id,
      );
      return {
        ...state,
        focusObjects: existing
          ? state.focusObjects.filter((item) => item.entityId !== entity.id)
          : [
              {
                id: `focus-user-${Date.now()}`,
                entityId: entity.id,
                type: entity.objectType,
                level: entity.riskLevel === 'none' ? 'low' : entity.riskLevel,
                followedAt: new Date().toISOString(),
              },
              ...state.focusObjects,
            ],
        activities: prependActivity(
          state,
          newActivity(
            state,
            `${existing ? '取消关注' : '关注对象'}：${entity.name}`,
            existing
              ? '已从重点关注中移除。'
              : '后续关键动态将进入关注信息流。',
            entity.id,
          ),
        ),
      };
    }
    case 'reminder/read':
    case 'reminder/snooze':
    case 'reminder/ignore': {
      const status =
        action.type === 'reminder/read'
          ? 'read'
          : action.type === 'reminder/snooze'
            ? 'snoozed'
            : 'ignored';
      return {
        ...state,
        reminders: state.reminders.map((item) =>
          item.id === action.payload.id ? { ...item, status } : item,
        ),
      };
    }
    case 'reminder/createTask': {
      const reminder = state.reminders.find(
        (item) => item.id === action.payload.id,
      );
      if (!reminder) return state;
      const entity = state.sourceEntities.find(
        (item) => item.id === reminder.entityId,
      );
      return workbenchReducer(state, {
        type: 'workItem/create',
        payload: {
          id: `task-reminder-${Date.now()}`,
          title: reminder.title,
          sourceApp: reminder.sourceApp,
          entityId: reminder.entityId,
          relatedObject: entity?.name ?? '提醒事项',
          type: '提醒跟进',
          priority: reminder.level === 'urgent' ? 'high' : 'medium',
          owner: '张伟',
          initiator: '张伟',
          dueAt: dayjs().add(1, 'day').hour(18).minute(0).toISOString(),
          status: 'notStarted',
          nextAction: reminder.suggestion,
          general: true,
        },
      });
    }
    case 'source/handleRisk': {
      const entity = state.sourceEntities.find(
        (item) => item.id === action.payload.entityId,
      );
      if (!entity || entity.riskHandled) return state;
      return {
        ...state,
        sourceEntities: state.sourceEntities.map((item) =>
          item.id === entity.id
            ? { ...item, riskHandled: true, latestChange: '风险已进入处置跟踪' }
            : item,
        ),
        reminders: state.reminders.map((item) =>
          item.entityId === entity.id ? { ...item, status: 'read' } : item,
        ),
        activities: prependActivity(
          state,
          newActivity(
            state,
            `风险处置：${entity.name}`,
            '风险已进入处置跟踪，高风险统计已同步。',
            entity.id,
          ),
        ),
      };
    }
    case 'sourceEntities/set':
      return { ...state, sourceEntities: action.payload };
    case 'preference/update':
      return {
        ...state,
        preference: { ...state.preference, ...action.payload },
      };
    case 'view/update':
      return { ...state, viewState: { ...state.viewState, ...action.payload } };
    case 'state/import':
    case 'state/reset':
      return action.payload;
    default:
      return state;
  }
};
